/**
 * piano-sviluppo-pdf-service.ts
 * 
 * Servizio per la generazione del PDF Piano di Sviluppo di Decoro Personalizzato
 */

import { apiClient } from './api';
import { RequestType } from '../types/api';

// DTOs per le sezioni del PDF
export interface BozzettoSection {
  riassumeVerificaTecnicaElementiIngresso?: string;
  sviluppoBozzetto?: string;
  riassumeVerificaBozzettoData?: string;
  riassumeVerificaBozzettoFirma?: string;
  riferimentoProgetto?: string;
  numeroTavole?: string;
  statoProgetto?: string;
}

export interface CampioneSection {
  riassumeVerificaTecnicaElementiIngresso?: string;
  sviluppoCampioneImpiantiColoriSpecificheTecniche?: string;
  riassumeVerificaCampioneData?: string;
  riassumeVerificaCampioneFirma?: string;
  riferimentoProgetto?: string;
  codiceDecoroOdv?: string;
  statoProgetto?: string;
}

export interface PersonalizzatoProduzioneSection {
  approvazioneBozzettoCampione?: string;
  sviluppoImpiantiPellicole?: string;
  riassumeVerificaSviluppoDecoroData?: string;
  riassumeVerificaSviluppoDecoroFirma?: string;
  riferimentoProgetto?: string;
  codiceDecoroOdv?: string;
  statoProgetto?: string;
}

export interface TestConformitaSection {
  data?: string;
  firma?: string;
}

export interface ValidazioneProgettoSection {
  data?: string;
  firma?: string;
}

export interface ProgressiveNumberResponse {
  number: number;
  year: number;
  formattedNumber: string;
}

export interface GeneratePianoSviluppoPdfRequest {
  requestId: number;
  nomeProgetto: string;
  numeroProgressivo: string;
  data: string;
  formaBianco?: string;
  requestType: RequestType;
  bozzettoSection?: BozzettoSection;
  campioneSection?: CampioneSection;
  personalizzatoProduzioneSection?: PersonalizzatoProduzioneSection;
  notes?: string;
  qualita?: string;
  testConformitaSection: TestConformitaSection;
  validazioneProgettoSection: ValidazioneProgettoSection;
}

class PianoSviluppoPdfService {
  /**
   * Ottiene o crea il numero progressivo per una richiesta specifica
   */
  async getOrCreateProgressiveNumber(requestId: number, requestType: RequestType, year: number): Promise<ProgressiveNumberResponse> {
    const response = await apiClient.post<ProgressiveNumberResponse>('/api/PianoSviluppoPdf/progressive-number', {
      requestId,
      requestType,
      year,
    });
    return response as any;
  }

  /**
   * Genera e scarica il PDF del Piano di Sviluppo
   */
  async generateAndDownloadPdf(request: GeneratePianoSviluppoPdfRequest): Promise<void> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'}/api/PianoSviluppoPdf/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error('Errore durante la generazione del PDF');
      }

      // Ottieni il blob del PDF
      const blob = await response.blob();
      
      // Estrai il nome del file dall'header Content-Disposition se presente
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Piano_Sviluppo_${request.nomeProgetto.replace(/\s+/g, '_')}_${request.numeroProgressivo.replace('/', '_')}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Crea un link temporaneo e avvia il download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore durante il download del PDF:', error);
      throw error;
    }
  }
}

export const pianoSviluppoPdfService = new PianoSviluppoPdfService();
